import type { TranscriptCorrection } from "@rosc/contracts";
import type { TFunction } from "i18next";

// SHA-256 (hex) of the transcript text, the API's optimistic-concurrency
// version (it hashes the stored text the same way).
export const transcriptVersion = async (text: string): Promise<string> => {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(text),
  );
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

// A diarization label at the start of a line ("Speaker 1 [Clinician]:").
const LABEL = /^(Speaker \d{1,2}(?: \[[^\]\n]*\])?:)[ \t]?/;
// The same label inside a line (a transcript written as one paragraph,
// "Speaker 1: Hi. Speaker 2: Hello."), after the whitespace before it.
const INLINE_LABEL = /([ \t]+)(?=Speaker \d{1,2}(?: \[[^\]\n]*\])?:(?:\s|$))/g;
export type TranscriptLine = {
  // The stored line, written back unchanged when its text was not edited.
  original: string;
  label: string | null;
  text: string;
  // What joins it to the line before: "\n", the whitespace before an inline
  // speaker label, or "" for the first line.
  joiner: string;
};
const toLine = (original: string, joiner: string): TranscriptLine => {
  const match = original.match(LABEL);
  return {
    original,
    label: match?.[1] ?? null,
    text: match ? original.slice(match[0].length) : original,
    joiner,
  };
};
// One entry per speaker turn: each line, split again at inline speaker
// labels, so a one-paragraph transcript still edits turn by turn.
export const splitTranscript = (text: string): TranscriptLine[] =>
  text.split("\n").flatMap((line, lineIndex) => {
    const parts: TranscriptLine[] = [];
    let start = 0;
    let joiner = lineIndex === 0 ? "" : "\n";
    for (const match of line.matchAll(INLINE_LABEL)) {
      const at = match.index ?? 0;
      if (at === 0) continue;
      parts.push(toLine(line.slice(start, at), joiner));
      joiner = match[1] ?? " ";
      start = at + joiner.length;
    }
    parts.push(toLine(line.slice(start), joiner));
    return parts;
  });
// Lines whose text is unchanged keep their exact bytes (and their joiners).
export const joinTranscript = (
  lines: readonly TranscriptLine[],
  edited: readonly string[],
): string =>
  lines
    .map((line, index) => {
      const text = edited[index] ?? line.text;
      if (text === line.text) return line.joiner + line.original;
      const oneLine = text.replace(/\s*\n\s*/g, " ");
      return line.joiner + (line.label ? `${line.label} ${oneLine}` : oneLine);
    })
    .join("");

const words = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
// The start time (ms) of each line, from the timed segments of the same
// transcript: a line takes the time of the first segment found in it, in
// order. Lines no segment maps to get null (the transcript has no timings,
// or they no longer match).
export const lineStartTimes = (
  lines: readonly TranscriptLine[],
  segments: readonly { startMs: number; text: string }[],
): (number | null)[] => {
  const times: (number | null)[] = lines.map(() => null);
  const texts = lines.map((line) => words(line.text));
  let at = 0;
  for (const segment of segments) {
    const needle = words(segment.text).split(" ").slice(0, 6).join(" ");
    if (!needle) continue;
    for (let index = at; index < texts.length; index += 1) {
      if (texts[index]?.includes(needle)) {
        if (times[index] === null) times[index] = segment.startMs;
        at = index;
        break;
      }
    }
  }
  return times;
};

// Same bound as noteEditProposalInputSchema.instruction.
const MAX_INSTRUCTION = 4000;
const MAX_SNIPPET = 300;
const clip = (value: string): string =>
  value.length > MAX_SNIPPET ? `${value.slice(0, MAX_SNIPPET - 1)}…` : value;

// The note assistant instruction for a transcript correction: the changed
// words, then "update only where the note depends on them". Corrections that
// do not fit the instruction limit are left out and counted.
export const correctionInstruction = (
  t: TFunction,
  corrections: readonly TranscriptCorrection[],
): { instruction: string; included: number; omitted: number } => {
  const intro = t("visit.transcriptEdit.instruction.intro");
  const outro = t("visit.transcriptEdit.instruction.outro");
  const lines: string[] = [];
  let length = intro.length + outro.length + 2;
  for (const correction of corrections) {
    const before = clip(correction.before),
      after = clip(correction.after);
    const line = `- ${
      !before
        ? t("visit.transcriptEdit.instruction.added", { after })
        : !after
          ? t("visit.transcriptEdit.instruction.removed", { before })
          : t("visit.transcriptEdit.instruction.changed", { before, after })
    }`;
    if (length + line.length + 1 > MAX_INSTRUCTION) break;
    lines.push(line);
    length += line.length + 1;
  }
  return {
    instruction: [intro, ...lines, outro].join("\n"),
    included: lines.length,
    omitted: corrections.length - lines.length,
  };
};
